// XSM-1563: Account settings hook - profile/password state split and form validation
// Author: Abhishek Bharti
// Date: 30/07/2026
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useDispatch } from "@/redux/store";
import { getCookie } from "@/utils";
import { fetchLoggedUser } from "@/utils/common";
import {
  fetchUserDetails,
  getPasswordPolicy,
  recoverPassword,
  updateUser
} from "@/redux/apps/users/UserSlice";
import { showMoreMessage, showSuccessMessage } from "@/redux/apps/toast/ToastSlice";
import { ScreenMapping, ToastMessages } from "@/libs/ToastMessages";
import { validatePhoneNumberGoogle } from "@/utils/helpers";
import { getSubdomain } from "@/utils/common";
import subDomainClientMapping from "@/config/domainMapping";

const parseMobileNumber = (mobileNumber?: string | null) => {
  if (!mobileNumber?.trim()) {
    return { mobile_number: "", country_code: "+91" };
  }

  const phone = mobileNumber.trim().split(" ");
  if (phone.length > 1) {
    return {
      mobile_number: phone.slice(1).join(" "),
      country_code: phone[0]
    };
  }

  return { mobile_number: phone[0], country_code: "+91" };
};

export const useAccountSettings = () => {
  const dispatch = useDispatch();
  const [profileEditMode, setProfileEditMode] = useState(false); // XSM-1563: Decouple profile edit mode from password section
  const [passwordValue, setPasswordValue] = useState("password");
  const [showPassMngt, setShowPassMngt] = useState(false);
  const [errors, setErrors] = useState(false);
  const domainName = getCookie("xsm_user_domain");
  const subDomain = getSubdomain();
  const { redirectURL } = subDomainClientMapping(subDomain);
  const callbackUrl = redirectURL.replace(/\/+$/, "");
  const [resetLinkSend, setResetLinkSend] = useState(false);
  const loggedUser = getCookie("xsm_user_details")
    ? JSON.parse(getCookie("xsm_user_details") || "")
    : null;
  const loggedUserName = loggedUser?.user_name ?? "";
  const [schema, setSchema] = useState<any>();
  const [passwordSchema, setPasswordSchema] = useState<any>();

  const generateProfileValidationSchema = () => {
    return Yup.object().shape({
      first_name: Yup.string()
        .required("First Name is Required")
        .min(3, "First Name Must be greater than 3 letters")
        .max(25, "First Name Must be less than 25 letters"),
      last_name: Yup.string()
        .required("Last Name is Required")
        .min(3, "Last Name Must be greater than 3 letters")
        .max(25, "Last Name Must be less than 25 letters"),
      email_address: Yup.string().email("Invalid email").required("Email is Required"),
      mobile_number: Yup.string().required("Phone number is Required")
    });
  };

  const generatePasswordValidationSchema = (regexPattern: any, regexText?: any) => {
    const regexObject = new RegExp(
      regexPattern?.slice(1, -1) || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/
    );
    const regexValidationText =
      regexText ||
      "Should have at least one Upper case and one Lower Case and Should have at least one Number";
    return Yup.object().shape({
      password: Yup.string()
        .required("Password is Required")
        .matches(regexObject, regexValidationText),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm Password is Required")
        .matches(regexObject, regexValidationText)
    });
  };

  const getDynamicSchema = async () => {
    const response: any = await dispatch(getPasswordPolicy({ domainName }));
    const regex = response.payload?.result?.pass_reg_expression;
    const regexText = response.payload?.result?.pass_regex_message.join("");
    return {
      profileSchema: generateProfileValidationSchema(),
      passwordSchema: generatePasswordValidationSchema(regex, regexText)
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (document.URL.includes("account-settings")) {
        try {
          const { profileSchema, passwordSchema: passSchema } = await getDynamicSchema();
          setSchema(profileSchema);
          setPasswordSchema(passSchema);
        } catch (error) {
          // Handle errors here if necessary
        }
      }
    };

    fetchData();
    fetchUserData();
  }, []);

  const accountSettingFormik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email_address: "",
      mobile_number: "",
      country_code: "+91",
      password: "",
      confirm_password: ""
    },
    onSubmit: () => { },
    validationSchema: schema
  });

  const { values, setFormikState }: any = accountSettingFormik;

  const isPhoneNumberValid = (formValues = accountSettingFormik.values) => {
    // XSM-1563: Validate phone using current Formik values at submit time
    if (!formValues.mobile_number?.trim()) {
      return false;
    }
    return validatePhoneNumberGoogle(formValues.country_code, formValues.mobile_number);
  };

  const valid = isPhoneNumberValid();

  const applyPhoneValidationErrors = (
    formErrors: Record<string, string>,
    formValues = accountSettingFormik.values
  ) => {
    // XSM-1563: Merge Yup field errors with phone format validation errors
    const nextErrors = { ...formErrors };

    if (!formValues.mobile_number?.trim()) {
      nextErrors.mobile_number = nextErrors.mobile_number || "Phone number is Required";
    } else if (!isPhoneNumberValid(formValues)) {
      nextErrors.mobile_number = "Phone number is not valid";
    }

    return nextErrors;
  };

  async function fetchUserData() {
    const response = await dispatch(
      fetchUserDetails({
        domainName,
        userName: loggedUser?.email_address || loggedUser?.user_name || fetchLoggedUser()
      })
    );
    const userDetails = response.payload?.result?.users[0];
    if (!userDetails) {
      return response;
    }

    const { mobile_number, country_code } = parseMobileNumber(
      userDetails?.mobile_number || loggedUser?.mobile_number
    );

    setFormikState((state: any) => ({
      ...state,
      values: {
        ...state.values,
        // XSM-1563: Explicitly map user profile fields to prevent form reset on refresh
        first_name: userDetails.first_name ?? state.values.first_name,
        last_name: userDetails.last_name ?? state.values.last_name,
        email_address: userDetails.email_address ?? state.values.email_address,
        status: userDetails.status ?? state.values.status,
        user_uuid: userDetails.user_uuid ?? state.values.user_uuid,
        mobile_number,
        country_code,
        password: "",
        confirm_password: ""
      }
    }));
    return response;
  }

  const handleOpenPasswordManagement = () => {
    // XSM-1563: Open password panel without altering profile edit state
    setShowPassMngt(true);
    accountSettingFormik.setFieldValue("password", "");
    accountSettingFormik.setFieldValue("confirm_password", "");
  };

  const handleClosePasswordManagement = () => {
    // XSM-1563: Close password panel and reset password field errors
    setShowPassMngt(false);
    setErrors(false);
    accountSettingFormik.setFieldValue("password", "");
    accountSettingFormik.setFieldValue("confirm_password", "");
    accountSettingFormik.setErrors({});
  };

  const profileFields = ["first_name", "last_name", "email_address", "mobile_number"] as const;
  const passwordFields = ["password", "confirm_password"] as const;

  const touchFields = (fields: readonly string[]) => {
    fields.forEach((field) => accountSettingFormik.setFieldTouched(field, true, false));
  };

  const getPasswordValidationErrors = async () => {
    if (!passwordSchema) {
      return { password: "Password policy is not loaded" };
    }

    try {
      await passwordSchema.validate(values, { abortEarly: false });
      return {};
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        return error.inner.reduce<Record<string, string>>((acc, validationError) => {
          if (validationError.path) {
            acc[validationError.path] = validationError.message;
          }
          return acc;
        }, {});
      }
      return {};
    }
  };

  const validateProfileForm = async () => {
    // XSM-1563: Validate profile fields before profile save or recover-password
    touchFields(profileFields);
    const formValues = accountSettingFormik.values;
    const formErrors = await accountSettingFormik.validateForm();
    const mergedErrors = applyPhoneValidationErrors(formErrors, formValues);
    const hasProfileErrors = Boolean(
      mergedErrors.first_name ||
      mergedErrors.last_name ||
      mergedErrors.email_address ||
      mergedErrors.mobile_number
    );

    if (hasProfileErrors) {
      accountSettingFormik.setErrors(mergedErrors);
      setErrors(true);
      return false;
    }

    return true;
  };

  const validatePasswordForm = async () => {
    // XSM-1563: Validate profile and password fields on password confirm
    touchFields([...profileFields, ...passwordFields]);

    const formValues = accountSettingFormik.values;
    const profileErrors = await accountSettingFormik.validateForm();
    const passwordErrors = await getPasswordValidationErrors();
    const mergedErrors = applyPhoneValidationErrors(
      { ...profileErrors, ...passwordErrors },
      formValues
    );

    const hasProfileErrors = Boolean(
      mergedErrors.first_name ||
      mergedErrors.last_name ||
      mergedErrors.email_address ||
      mergedErrors.mobile_number
    );
    const hasPasswordErrors = Boolean(mergedErrors.password || mergedErrors.confirm_password);

    if (hasProfileErrors || hasPasswordErrors) {
      accountSettingFormik.setErrors(mergedErrors);
      setErrors(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async (isPasswordUpdate?: boolean) => {
    if (isPasswordUpdate) {
      const isValid = await validatePasswordForm();
      if (!isValid) {
        return;
      }
    } else {
      const isValid = await validateProfileForm();
      if (!isValid) {
        return;
      }
    }

    const data = {
      dom_name: domainName,
      user_mobile_number: `${values.country_code} ${values.mobile_number}`,
      user_password: isPasswordUpdate ? values.password : null,
      modified_by: loggedUserName,
      status: values.status,
      userId: values?.user_uuid,
      user_last_name: values?.last_name,
      user_first_name: values?.first_name
    };

    const response: any = await dispatch(updateUser(data));
    if (response.payload?.isSuccess) {
      await fetchUserData();
      setProfileEditMode(false);
      setShowPassMngt(false);
      setErrors(false);
      dispatch(
        showSuccessMessage(
          ToastMessages.UPDATED.replace(":screen", ScreenMapping.USER).replace(
            ":name",
            values.first_name
          )
        )
      );
    } else {
      dispatch(
        showMoreMessage({
          ...response.payload,
          message: ToastMessages.FAILED_UPDATED.replace(":screen", ScreenMapping.USER)
        })
      );
    }
  };

  const handleResetPassword = async () => {
    // XSM-1563: Validate profile and phone before calling recover-password API
    if (passwordValue === "passwordLink") {
      const isValid = await validateProfileForm();
      if (!isValid) {
        return;
      }

      const data = {
        email: values.email_address || loggedUser?.email_address,
        callbackurl: callbackUrl
      };
      const response: any = await dispatch(recoverPassword(data));
      if (response.payload?.isSuccess) {
        dispatch(showSuccessMessage(response.payload?.message));
        setResetLinkSend(true);
      } else {
        dispatch(showMoreMessage(response.payload));
      }
    } else {
      handleSubmit(true);
    }
  };

  return {
    accountSettingFormik,
    profileEditMode,
    setProfileEditMode,
    passwordValue,
    setPasswordValue,
    values: accountSettingFormik.values,
    setShowPassMngt,
    showPassMngt,
    loggedUser,
    errors,
    setErrors,
    valid,
    handleSubmit,
    handleResetPassword,
    handleOpenPasswordManagement,
    handleClosePasswordManagement,
    resetLinkSend
  };
};
